import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useContactConversion } from 'hooks/useContactConversion';
import usePaginationByItem from 'hooks/usePaginationByItem';
import strings from 'strings';
import { FPIconButton } from 'UI/components/atoms';
import { When } from 'UI/components/atoms/When';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { EntityType } from 'UI/constants/entityTypes';
import FPCandidateProfile from 'UI/pages/CandidateProfile';
import FPCompanyProfile from 'UI/pages/CompanyProfile';
import ContactProfileContent from 'UI/pages/ContactProfile/ContactProfileContent';
import HiringAuthorityProfile from 'UI/pages/HiringAuthorityProfile';
import FPJobOrderProfile from 'UI/pages/JobOrderProfile';
import { SvgArrowLeft, SvgArrowRight } from 'UI/res';
import { getEntityType } from 'UI/utils/inventory';

import { PROFILE_ENTITIES_PROPS } from '../commons';

import { useStyles } from './styles';
import { handleAssignEntityRole } from './utils';

/** @typedef {import('@material-ui/core').DrawerProps} DrawerProps */

const PRIMARY_BUTTON_TEXT = strings.inventoryProfiles.quickView.initialText;
const NAVIGATION_CONTROLS_ICON_SIZE = 14;

const ProfileWrapper = ({
  conversionStatus,
  profileProps: { enableUrlNavigation, layoutClassName },
  profileModeProps,
  onEditionCompleted
}) => {
  return conversionStatus?.candidate ? (
    <FPCandidateProfile
      id={conversionStatus?.candidate?.id}
      enableUrlNavigation={enableUrlNavigation}
      layoutClassName={layoutClassName}
      onEditionCompleted={onEditionCompleted}
      {...profileModeProps}
    />
  ) : (
    <HiringAuthorityProfile
      id={conversionStatus?.hiringAuthority?.id}
      enableUrlNavigation={enableUrlNavigation}
      layoutClassName={layoutClassName}
      onEditionCompleted={onEditionCompleted}
      {...profileModeProps}
    />
  );
};

const ConvertedContact = ({
  profileProps: { nameId, enableUrlNavigation, layoutClassName },
  profileModeProps,
  onEditionCompleted
}) => {
  const { entityConversionState, isSuccess, shouldRenderProfileContent, enableConvertedContacts } =
    useContactConversion({
      nameId
    });

  const conversionStatus = entityConversionState.results;

  const classes = useStyles();

  const profileContent = (
    <ContactProfileContent
      id={nameId}
      layoutClassName={layoutClassName}
      enableUrlNavigation={enableUrlNavigation}
      onEditionCompleted={onEditionCompleted}
    />
  );

  const componentRender = shouldRenderProfileContent ? (
    profileContent
  ) : (
    <ProfileWrapper
      conversionStatus={conversionStatus}
      profileProps={{ nameId, enableUrlNavigation, layoutClassName }}
      profileModeProps={profileModeProps}
      onEditionCompleted={onEditionCompleted}
    />
  );

  return (
    <>
      <When condition={enableConvertedContacts}>
        <When condition={isSuccess}>{componentRender}</When>
        <When condition={!isSuccess}>
          <div className={classes.circularProgressContainer}>
            <CircularProgress />
          </div>
        </When>
      </When>
      <When condition={!enableConvertedContacts}>{profileContent}</When>
    </>
  );
};

/**
 * @param {Object} props
 * @param {string} props.entityType
 * @param {number} props.id
 * @param {DrawerProps} props.drawerProps
 * @param {Object} props.NavigationControlsProps
 * @param {Object} props.quickViewEntityRoleProps
 * @param {Function} props.onEditionCompleted
 * @param {Function} props.onVerificationCompleted
 */
const FPQuickView = ({
  entityType,
  id,
  drawerProps,
  itemsToNavigate,
  onClose,
  quickViewEntityRoleProps = {},
  NavigationControlsProps: { enableNavigationControls },
  onEditionCompleted,
  onVerificationCompleted = undefined
}) => {
  const classes = useStyles();

  const [relationId, setRelationId] = useState(null);

  const { goToNext, goToPrevious, selectedProfileId, hasNextItem, hasPreviousItem, currentIndex } =
    usePaginationByItem(itemsToNavigate, id);

  const profileEntity = getEntityType(entityType);
  const [currentEntity, setCurrentEntity] = useState(profileEntity?.id);
  const activeProfileId = relationId || selectedProfileId;

  const sharedProps = {
    id: activeProfileId,
    layoutClassName: classes.layout,
    enableUrlNavigation: false,
    onEditionCompleted,
    onVerificationCompleted
  };

  const handleQuickViewSwitch = (updatedId, mode) => {
    setCurrentEntity(mode);
    setRelationId(updatedId);
  };

  const profileModeProps = {
    profileModeProps: {
      toggleProfileQuickViewProps: {
        isQuickViewMode: true,
        onQuickViewOptionClick: handleQuickViewSwitch,
        mode: currentEntity
      }
    }
  };

  const profiles = {
    [EntityType.Candidate]: <FPCandidateProfile {...sharedProps} {...profileModeProps} />,
    [EntityType.Company]: (
      <FPCompanyProfile
        profileId={selectedProfileId}
        layoutClassName={classes.layout}
        enableUrlNavigation={false}
        onEditionCompleted={sharedProps.onEditionCompleted}
      />
    ),
    [EntityType.Contact]: (
      <ConvertedContact
        profileProps={{
          nameId: sharedProps.id,
          layoutClassName: sharedProps.layoutClassName,
          enableUrlNavigation: sharedProps.enableUrlNavigation
        }}
        profileModeProps={profileModeProps}
        onEditionCompleted={sharedProps.onEditionCompleted}
      />
    ),
    [EntityType.HiringAuthority]: <HiringAuthorityProfile {...sharedProps} {...profileModeProps} />,
    [EntityType.Joborder]: <FPJobOrderProfile {...sharedProps} />
  };

  const resetNavigation = () => {
    setCurrentEntity(profileEntity?.id);
    setRelationId(null);
  };

  const handleGoBack = () => {
    resetNavigation();
    goToPrevious();

    if (quickViewEntityRoleProps) {
      handleAssignEntityRole({
        item: itemsToNavigate[currentIndex - 1],
        quickViewEntityRoleProps,
        profileEntity,
        onSetCurrentEntity: setCurrentEntity
      });
    }
  };

  const handleGoNext = () => {
    resetNavigation();
    goToNext();

    if (quickViewEntityRoleProps) {
      handleAssignEntityRole({
        item: itemsToNavigate[currentIndex + 1],
        quickViewEntityRoleProps,
        profileEntity,
        onSetCurrentEntity: setCurrentEntity
      });
    }
  };

  const shouldEnableNavigationControls = itemsToNavigate?.length >= 2 && enableNavigationControls;

  const { profileRoute } = PROFILE_ENTITIES_PROPS[currentEntity];
  const shouldUseRelationRouteId = [EntityType.Candidate, EntityType.HiringAuthority].includes(
    currentEntity
  );
  const profileRouteId = shouldUseRelationRouteId ? activeProfileId : selectedProfileId;

  const buttonDefinitions = [
    {
      props: {
        disabled: !hasPreviousItem,
        icon: SvgArrowLeft,
        onClick: () => handleGoBack(),
        iconProps: { size: NAVIGATION_CONTROLS_ICON_SIZE },
        text: strings.shared.buttons.previous,
        variant: 'outlined'
      },
      visible: shouldEnableNavigationControls
    },
    {
      props: {
        disabled: !hasNextItem,
        icon: SvgArrowRight,
        iconPosition: 'right',
        onClick: () => handleGoNext(),
        iconProps: { size: NAVIGATION_CONTROLS_ICON_SIZE },
        text: strings.shared.buttons.next,
        variant: 'outlined'
      },
      visible: shouldEnableNavigationControls
    },
    {
      props: { text: strings.shared.ui.close, variant: 'outlined', onClick: onClose },
      visible: !shouldEnableNavigationControls
    },
    {
      props: {
        text: PRIMARY_BUTTON_TEXT,
        component: RouterLink,
        target: '_blank',
        to: profileRoute.replace(':id', profileRouteId)
      },
      mode: 'default',
      visible: selectedProfileId
    }
  ];

  return (
    <FPDrawer
      key={selectedProfileId}
      onClose={onClose}
      hasPaddingContent={false}
      header={
        <div className={classes.closeIconContainer}>
          <FPIconButton
            tooltipProps={{ placement: 'bottom', title: strings.shared.ui.close }}
            onClick={onClose}
            icon={HighlightOffIcon}
          />
        </div>
      }
      FPDrawerProps={{ open: true, classes: { paper: classes.drawerPanel }, ...drawerProps }}
      contentContainerClassName={classes.drawerContainer}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      {id && selectedProfileId && profiles[currentEntity]}
    </FPDrawer>
  );
};

FPQuickView.defaultProps = {
  NavigationControlsProps: { enableNavigationControls: true },
  quickViewEntityRoleProps: null
};

export default FPQuickView;
